import React, { useMemo } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import {
  useTimePeriods,
  useRetailerId,
  useProductEntity,
  useCategoryEntity,
  useSubcategoryEntity,
  useBrandEntity
} from 'src/utils/Hooks/reduxSelectors';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { VariationsGaugeCards } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsGaugeCards';
import { VariationsSplineChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsSplineChart';
import { VariationsParentPageCountSplineChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsParentPageCountSplineChart';
import { VariationsByCategoryBarChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsByCategoryBarChart';
import { VariationsParentPageCountByCategoryBarChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsParentPageCountByCategoryBarChart';
import { VariationsPageSizeCards } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsPageSizeCards';
import VariationsProductDetailsGridCards from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsProductDetailsGridCards';
import { useDynamicVariationsSubtitles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useDynamicVariationsSubtitles';
import { useStacklineTheme } from '@stackline/ui';
import {
  CategoryType,
  VariationsMetricsByCategoryRequestType,
  VariationsDetailGridMetricType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { VariationsByCategoryAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsByCategoryAdapter';
import { TimePeriod } from 'src/types/store/storeTypes';
import { VariationsFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { GroupByFieldType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/stores/variationsAnalyticStore';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

const variationBarChartMetrics = [
  { id: VariationsFilter.ChildVariationRate, label: 'Child Variation Rate' },
  { id: VariationsFilter.SingleSkuPages, label: 'Unvariated Child SKUs' },
  { id: VariationsFilter.VariatedSkuPages, label: 'Variated Child SKUs' },
  { id: VariationsFilter.ChildAccuracy, label: 'Child SKU Accuracy' },
  { id: VariationsFilter.IncorrectChildSkus, label: 'Inaccurate Child SKUs' },
  { id: VariationsFilter.CorrectChildSkus, label: 'Accurate Child SKUs' },
  { id: VariationsFilter.ParentPageAccuracy, label: 'Parent Page Accuracy' },
  { id: VariationsFilter.InaccurateParentPages, label: 'Inaccurate Parent Pages' },
  { id: VariationsFilter.PageDensity, label: 'Page Density' },
  { id: VariationsFilter.ParentPageCount, label: 'Parent Page Count' }
];

const groupByFields = [
  { id: GroupByFieldType.Category, label: 'Category' },
  { id: GroupByFieldType.Subcategory, label: 'Subcategory' },
  { id: GroupByFieldType.Brand, label: 'Brand' }
];

const createVariationsByCategoryQueryFnMap = (
  getVariationsByCategory: (
    requestType: VariationsMetricsByCategoryRequestType,
    categoryType: CategoryType,
    mainTimePeriod: TimePeriod,
    comparisonTimePeriod: TimePeriod
  ) => Promise<VariationsByCategoryAdapter>,
  mainTimePeriod: TimePeriod,
  comparisonTimePeriod: TimePeriod
) => {
  const requestTypes = [
    VariationsMetricsByCategoryRequestType.VariationAccuracyByCategory,
    VariationsMetricsByCategoryRequestType.VariatedSkuCountByCategory,
    VariationsMetricsByCategoryRequestType.SingleSkuCountByCategory,
    VariationsMetricsByCategoryRequestType.ChildAccuracyByCategory,
    VariationsMetricsByCategoryRequestType.CorrectChildCountByCategory,
    VariationsMetricsByCategoryRequestType.IncorrectChildCountByCategory,
    VariationsMetricsByCategoryRequestType.ParentPageAccuracyByCategory,
    VariationsMetricsByCategoryRequestType.InaccurateParentCountByCategory,
    VariationsMetricsByCategoryRequestType.PageDensityByCategory,
    VariationsMetricsByCategoryRequestType.ParentPageCountByCategory
  ];

  return requestTypes.reduce((acc, requestType) => {
    acc[requestType] = {
      [GroupByFieldType.Category]: () => {
        const categoryType = CategoryType.Category;
        return getVariationsByCategory(requestType, categoryType, mainTimePeriod, comparisonTimePeriod);
      },
      [GroupByFieldType.Subcategory]: () => {
        const categoryType = CategoryType.Subcategory;
        return getVariationsByCategory(requestType, categoryType, mainTimePeriod, comparisonTimePeriod);
      },
      [GroupByFieldType.Brand]: () => {
        const categoryType = CategoryType.Brand;
        return getVariationsByCategory(requestType, categoryType, mainTimePeriod, comparisonTimePeriod);
      }
    };
    return acc;
  }, {} as Record<VariationsMetricsByCategoryRequestType, Record<GroupByFieldType, () => Promise<VariationsByCategoryAdapter>>>);
};

const extractParentPageCountQueryFnMap = (
  queryFnMap: Record<
    VariationsMetricsByCategoryRequestType,
    Record<GroupByFieldType, () => Promise<VariationsByCategoryAdapter>>
  >
) => {
  return queryFnMap[VariationsMetricsByCategoryRequestType.ParentPageCountByCategory];
};

const ContentVariationsPage = () => {
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const retailerId = useRetailerId();
  const { isProductEntity } = useProductEntity();
  const {
    getVariationsOverviewMetricsForGaugeCards,
    getVariationsMetricsForSplineChart,
    getVariationsByCategory,
    getVariationsPageSize,
    getVariationsProductDetailsGrid
  } = useContentService();
  const {
    generateVariationPerformanceSubtitle,
    generatePageSizeSubtitle,
    generateBarChartSubtitle,
    generateGridSubtitle
  } = useDynamicVariationsSubtitles();
  const theme = useStacklineTheme();

  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isBrandEntity } = useBrandEntity();

  const filteredGroupByFields = useMemo(() => {
    if (isBrandEntity) {
      return groupByFields.filter((field) => field.id === GroupByFieldType.Brand);
    } else if (isSubcategoryEntity) {
      return groupByFields.filter((field) => field.id === GroupByFieldType.Brand);
    } else if (isCategoryEntity && !isAllCategoriesEntity) {
      return groupByFields.filter(
        (field) => field.id === GroupByFieldType.Subcategory || field.id === GroupByFieldType.Brand
      );
    } else {
      return groupByFields;
    }
  }, [isCategoryEntity, isAllCategoriesEntity, isSubcategoryEntity, isBrandEntity]);

  const variationsByCategoryQueryFnMap = useMemo(() => {
    return createVariationsByCategoryQueryFnMap(getVariationsByCategory, mainTimePeriod, comparisonTimePeriod);
  }, [getVariationsByCategory, mainTimePeriod, comparisonTimePeriod]);

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: '104px' }}>
      <VariationsGaugeCards
        title="Variation Performance"
        predefinedColor={theme.colors.primary}
        queryFn={() => getVariationsOverviewMetricsForGaugeCards(mainTimePeriod, comparisonTimePeriod)}
        renderSubtitle={() => {
          return generateVariationPerformanceSubtitle();
        }}
      />

      <ChartContextProvider
        group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_VARIATIONS_SPLINE.name}
        chartName={CHART_NAMES.BEACON_CONTENT_VARIATIONS_SPLINE_CHART}
      >
        <VariationsSplineChart
          metricType={METRICTYPE.PERCENT}
          queryFn={(filterKey) => getVariationsMetricsForSplineChart(filterKey, mainTimePeriod, comparisonTimePeriod)}
        />
      </ChartContextProvider>

      {!isProductEntity && (
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_VARIATIONS_BAR.name}
          chartName={CHART_NAMES.BEACON_CONTENT_VARIATIONS_BAR_CHART}
        >
          <VariationsByCategoryBarChart
            metrics={variationBarChartMetrics}
            groupByFields={filteredGroupByFields}
            queryFnMap={variationsByCategoryQueryFnMap}
            renderSubtitle={(selectedMetric, selectedGroupBy) => {
              const metricLabel =
                variationBarChartMetrics.find((metric) => metric.id === selectedMetric)?.label || selectedMetric;
              const groupByLabel =
                filteredGroupByFields.find((groupByField) => groupByField.id === selectedGroupBy)?.label ||
                selectedGroupBy;

              return generateBarChartSubtitle(metricLabel, groupByLabel);
            }}
          />
        </ChartContextProvider>
      )}

      <ChartContextProvider
        group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_VARIATIONS_PAGE_SIZE.name}
        chartName={CHART_NAMES.BEACON_CONTENT_VARIATIONS_PAGE_SIZE_CHART}
      >
        <VariationsPageSizeCards
          title="Page Size"
          queryFn={() => getVariationsPageSize(Number(retailerId), mainTimePeriod)}
          renderSubtitle={() => {
            return generatePageSizeSubtitle();
          }}
        />
      </ChartContextProvider>

      <ChartContextProvider
        group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_VARIATIONS_PARENT_PAGE_SPLINE.name}
        chartName={CHART_NAMES.BEACON_CONTENT_VARIATIONS_PARENT_PAGE_SPLINE_CHART}
      >
        <VariationsParentPageCountSplineChart
          queryFn={() =>
            getVariationsMetricsForSplineChart(VariationsFilter.ParentPageCount, mainTimePeriod, comparisonTimePeriod)
          }
        />
      </ChartContextProvider>

      {!isProductEntity && (
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_VARIATIONS_PARENT_PAGE_BAR.name}
          chartName={CHART_NAMES.BEACON_CONTENT_VARIATIONS_PARENT_PAGE_BAR_CHART}
        >
          <VariationsParentPageCountByCategoryBarChart
            groupByFields={filteredGroupByFields}
            queryFnMap={extractParentPageCountQueryFnMap(variationsByCategoryQueryFnMap)}
            renderSubtitle={(selectedGroupBy) => {
              const groupByLabel =
                filteredGroupByFields.find((groupByField) => groupByField.id === selectedGroupBy)?.label ||
                selectedGroupBy;

              return generateBarChartSubtitle('Parent Page Count', groupByLabel);
            }}
          />
        </ChartContextProvider>
      )}

      <div id={ContentSectionId.TableGrid} key={`${ContentSectionId.TableGrid}}`}>
        <VariationsProductDetailsGridCards
          title="Product Details"
          queryFn={(requestType, variationsFilterType, offset) =>
            getVariationsProductDetailsGrid(
              Number(retailerId),
              mainTimePeriod,
              comparisonTimePeriod,
              requestType,
              variationsFilterType,
              offset
            )
          }
          renderSubtitle={(dropdownOption) => {
            const dataType =
              dropdownOption === VariationsDetailGridMetricType.PageDetailGrid
                ? 'Page Detail by Parent SKU'
                : 'Page Detail by Child SKU';
            return generateGridSubtitle(dataType);
          }}
        />
      </div>
    </BasePageContainer>
  );
};

export default ContentVariationsPage;
